import { Namespace, useTypedT } from "@racoon/i18n";
import { cn } from "@racoon/ui";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import type { ScheduleFulfillmentBucket } from "../lib/use-schedule-fulfillment.hook";

const MetricBlock = ({
  label,
  value,
  percent,
  badgeColor,
}: {
  label: string;
  value: string | number;
  percent?: number;
  badgeColor?: string;
}) => (
  <div className="flex min-w-0 flex-1 flex-col gap-2 h-full justify-between">
    <p className="truncate text-xs font-medium text-text-secondary">{label}</p>
    {percent != null && badgeColor != null && (
      <span
        className={cn(
          "w-fit shrink-0 rounded-full px-2 py-0.5 text-xs font-bold uppercase text-white",
          badgeColor,
        )}
      >
        {percent}%
      </span>
    )}
    <p className="truncate text-[22px] font-bold leading-7 tracking-tight text-text-primary">
      {value}
    </p>
  </div>
);

const FulfillmentColumn = ({
  label,
  badgeColor,
  bucket,
  weightTons,
  className,
}: {
  label: string;
  badgeColor: string;
  bucket: ScheduleFulfillmentBucket;
  weightTons?: number;
  className?: string;
}) => {
  const t = useTypedT(Namespace.Govdash);

  return (
    <div className={cn("flex min-w-0 flex-1 flex-col gap-3 p-4", className)}>
      <p className="text-xs font-bold uppercase tracking-tight text-text-primary">{label}</p>
      <div className="flex min-w-0 items-start gap-4">
        <MetricBlock
          label={t("monitor.locationsShort")}
          value={bucket.locations}
          percent={bucket.locationsPercent}
          badgeColor={badgeColor}
        />
        <MetricBlock
          label={t("monitor.volumeShort")}
          value={t("monitor.volumeValue", { value: bucket.volumeM3.toFixed(0) })}
          percent={bucket.volumePercent}
          badgeColor={badgeColor}
        />
        {weightTons != null && (
          <MetricBlock
            label={t("monitor.weightShort")}
            value={t("monitor.tonsValue", { value: weightTons.toFixed(1) })}
          />
        )}
      </div>
    </div>
  );
};

export const ScheduleFulfillmentPanel = ({
  fact,
  plan,
  notExported,
  totalLocations,
  wasteTons,
}: {
  fact: ScheduleFulfillmentBucket;
  plan: ScheduleFulfillmentBucket;
  notExported: ScheduleFulfillmentBucket;
  totalLocations: number;
  wasteTons: number;
}) => {
  const t = useTypedT(Namespace.Govdash);

  return (
    // Fixed-width outer wrapper: CardContentWrapper's own `w-full` class must fill a
    // sized parent rather than be overridden in-place, since `cn` is a plain string
    // join (no Tailwind conflict resolution) and the later class isn't guaranteed to win.
    <div className="w-[520px] min-w-[520px] shrink-0 overflow-hidden flex-[2]">
      <CardContentWrapper hasDivider={false} className="flex h-full flex-col overflow-hidden">
        <div className="flex shrink-0 gap-3 border-b border-slate-200 p-4">
          <p className="text-base font-semibold text-text-primary flex-1">
            {t("monitor.scheduleFulfillment")}
          </p>
          {totalLocations > 0 && (
            <div className="flex h-6 w-full overflow-hidden rounded-lg flex-[2]">
              <div
                className="flex items-center justify-center bg-green-500"
                style={{ flex: fact.locations || 0.0001 }}
              >
                <span className="text-xs font-medium text-white">{fact.locations}</span>
              </div>
              <div
                className="flex items-center justify-center bg-blue-500"
                style={{ flex: plan.locations || 0.0001 }}
              >
                <span className="text-xs font-medium text-white">{plan.locations}</span>
              </div>
              <div
                className="flex items-center justify-center bg-red-500"
                style={{ flex: notExported.locations || 0.0001 }}
              >
                <span className="text-xs font-medium text-white">{notExported.locations}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-1 min-w-0">
          <FulfillmentColumn
            label={t("monitor.fact")}
            badgeColor="bg-green-500"
            bucket={fact}
            weightTons={wasteTons}
            className="border-r border-slate-200 flex-[3]"
          />
          <FulfillmentColumn
            label={t("monitor.plan")}
            badgeColor="bg-blue-500"
            bucket={plan}
            className="border-r border-slate-200 flex-[2]"
          />
          <FulfillmentColumn
            label={t("analytics.status.notExported")}
            badgeColor="bg-red-500"
            bucket={notExported}
            className="flex-[2]"
          />
        </div>
      </CardContentWrapper>
    </div>
  );
};
